// Shared react-query cache helper for the AI risk session
// (key ["aiRiskSession", projectId]) — used by the hazard-mutating hooks to
// merge a PATCH/generate response back into the cached session without a
// refetch.

import { QueryClient } from "react-query";

import { AIHazardPatch, AIHazardShell, AIRiskSession } from "../types";

export const AI_RISK_SESSION_QUERY_KEY = "aiRiskSession";

// Applies a PATCH result back into the cached session WITHOUT depending solely
// on the server echo. The backend SHOULD return the updated shell, but the
// PATCH response shape is the one thing the client can't fully rely on (204 No
// Content, a wrapped body, or the full session would all leave the echo
// unmatched). Since the contract guarantees persist-before-respond, on a 200
// the fields we sent ARE the durable state, so we overlay the sent `patch` on
// top of the (preferred) server echo. Without this, a diff apply that clears
// `pending_proposal: null` could silently no-op the cache and strand the user
// on the diff view even though the PATCH succeeded.
export const applyHazardPatchToSessionCache = (
  queryClient: QueryClient,
  projectId: string,
  hazardId: string,
  patch: AIHazardPatch,
  responseHazard: AIHazardShell
) => {
  queryClient.setQueryData<AIRiskSession | null>(
    [AI_RISK_SESSION_QUERY_KEY, projectId],
    (session) => {
      if (!session) {
        return session ?? null;
      }

      return {
        ...session,
        hazards: session.hazards.map((candidate) => {
          if (candidate.id !== hazardId) {
            return candidate;
          }

          // Prefer the echoed shell when the backend actually returned the
          // updated hazard; fall back to the cached one otherwise. Either way
          // the sent patch wins, so explicit fields (e.g. pending_proposal:
          // null) always take effect.
          const base =
            responseHazard && responseHazard.id === hazardId
              ? responseHazard
              : candidate;

          return { ...base, ...patch };
        }),
      };
    }
  );
};

export const mergeHazardIntoSessionCache = (
  queryClient: QueryClient,
  projectId: string,
  hazard: AIHazardShell
) => {
  queryClient.setQueryData<AIRiskSession | null>(
    [AI_RISK_SESSION_QUERY_KEY, projectId],
    (session) => {
      if (!session) {
        // No cached session (e.g. cache evicted) — nothing to merge into;
        // the next GET re-syncs from the persisted server state.
        return session ?? null;
      }

      return {
        ...session,
        hazards: session.hazards.map((candidate) =>
          candidate.id === hazard.id ? hazard : candidate
        ),
      };
    }
  );
};
